import {
  Box, Typography, Divider, Link,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
} from '@mui/material';
import LegalPageLayout from '../../components/legal/LegalPageLayout';

const COOKIE_ROWS = [
  { name: 'msal.cache.encryption', provider: 'Microsoft Authentication Library (MSAL)', purpose: 'Protects tokens stored in the browser', category: 'Strictly Necessary / Authentication', duration: 'Session' },
  { name: 'AADSSO', provider: 'Microsoft Entra ID', purpose: 'Detects an existing SSO session and avoids prompting the user for credentials again', category: 'Strictly Necessary / Authentication', duration: 'Session' },
  { name: 'ARRAffinity', provider: 'Microsoft Azure', purpose: 'Azure hosting/routing affinity', category: 'Strictly Necessary', duration: 'Session' },
  { name: 'ESTSAUTH', provider: 'Microsoft Entra ID', purpose: 'Authentication and security during the sign-in process', category: 'Strictly Necessary / Authentication', duration: 'Determined by Microsoft' },
  { name: 'ESTSAUTHLIGHT', provider: 'Microsoft Entra ID', purpose: 'Authentication and security during the sign-in process', category: 'Strictly Necessary / Authentication', duration: 'Determined by Microsoft' },
  { name: 'ESTSAUTHPERSISTENT', provider: 'Microsoft Entra ID', purpose: 'Authentication and security during the sign-in process', category: 'Strictly Necessary / Authentication', duration: 'Determined by Microsoft' },
  { name: 'SignInStateCookie', provider: 'Microsoft Entra ID', purpose: 'Supports authentication and sign-in state', category: 'Strictly Necessary / Authentication', duration: 'Determined by Microsoft' },
  { name: 'esctx', provider: 'Microsoft Entra ID', purpose: 'Supports authentication and security controls', category: 'Strictly Necessary / Authentication', duration: 'Determined by Microsoft' },
  { name: 'buid', provider: 'Microsoft Entra ID', purpose: 'Supports authentication and security functionality', category: 'Strictly Necessary / Authentication', duration: 'Determined by Microsoft' },
  { name: 'fpc', provider: 'Microsoft Entra ID', purpose: 'Supports authentication and security functionality', category: 'Strictly Necessary / Authentication', duration: 'Determined by Microsoft' },
  { name: 'stsservicecookie', provider: 'Microsoft Entra ID', purpose: 'Supports authentication and Microsoft service functionality', category: 'Strictly Necessary / Authentication', duration: 'Determined by Microsoft' },
  { name: 'x-ms-gateway-slice', provider: 'Microsoft Entra ID / Microsoft infrastructure', purpose: 'Supports authentication and routing functionality', category: 'Strictly Necessary / Authentication', duration: 'Determined by Microsoft' },
];

const NAVY = '#0a326d';
const TEXT = '#334155';

function Section({ number, title, children }) {
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" fontWeight={700} color={NAVY} sx={{ mb: 1 }}>
        {number}. {title}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>{children}</Box>
    </Box>
  );
}

function P({ children }) {
  return (
    <Typography variant="body2" sx={{ color: TEXT, lineHeight: 1.7, textAlign: 'justify' }}>
      {children}
    </Typography>
  );
}

const headCellSx = { fontWeight: 700, color: '#fff', whiteSpace: 'nowrap' };
const cellSx = { fontSize: 13, color: TEXT, verticalAlign: 'top' };

export default function CookiePolicy() {
  return (
    <LegalPageLayout>
      <Box sx={{ textAlign: 'center' }}>
        <Typography
          variant="h5"
          fontWeight={800}
          color="#072146"
          sx={{ letterSpacing: 0.3, fontSize: { xs: '1.15rem', sm: '1.4rem', md: '1.5rem' } }}
        >
          TIFOXR CMS COOKIE POLICY
        </Typography>
        <Typography variant="subtitle2" fontWeight={700} color={NAVY} sx={{ mt: 0.5 }}>
          Global Mobile Software LLC
        </Typography>
        <Typography variant="body2" color="#64748b" sx={{ mt: 0.5 }}>
          Last Updated: August 21, 2026
        </Typography>
      </Box>

      <Divider sx={{ my: 1 }} />

      <P>
        Questions about this Cookie Policy may be sent to{' '}
        <Link href="mailto:compliance@globalmobisoft.com" sx={{ fontWeight: 700 }}>
          compliance@globalmobisoft.com
        </Link>
        .
      </P>

      <Section number={1} title="Scope">
        <P>
          This Cookie Policy explains how Global Mobile Software LLC ("GMS," "we," "us," or "our") uses cookies and
          similar technologies in connection with the TifoXR CMS platform.
        </P>
        <P>
          TifoXR CMS is used by authorized club personnel, administrators, contractors, and other approved users to
          manage and publish TifoXR content, workflows, metadata, and related configurations.
        </P>
        <P>
          This Cookie Policy applies specifically to the TifoXR CMS platform. Separate cookie policies may apply to
          the public TifoXR website, TifoXR Immersive, club-specific deployments, or other GMS websites and product
          environments.
        </P>
      </Section>

      <Section number={2} title="What Are Cookies and Similar Technologies?">
        <P>
          Cookies are small files or pieces of information stored on a browser or device when you access a website
          or online service.
        </P>
        <P>
          Similar technologies may include local storage, session storage, software development kits (SDKs),
          authentication storage, and other technologies that store or access information on a device.
        </P>
        <P>
          These technologies may be used to authenticate authorized CMS users, maintain secure sessions, protect
          accounts and platform security, maintain CMS functionality, support authentication and sign-in, and
          support hosting and routing functionality.
        </P>
      </Section>

      <Section number={3} title="TifoXR CMS Platform Cookie List">
        <P>
          The following cookies and similar technologies have been identified for the current TifoXR CMS platform:
        </P>

        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #CBD5E1', borderRadius: 2, overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: NAVY }}>
                <TableCell sx={headCellSx}>Cookie / Technology</TableCell>
                <TableCell sx={headCellSx}>Provider</TableCell>
                <TableCell sx={headCellSx}>Purpose</TableCell>
                <TableCell sx={headCellSx}>Category</TableCell>
                <TableCell sx={headCellSx}>Duration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {COOKIE_ROWS.map((row, i) => (
                <TableRow key={row.name} sx={{ bgcolor: i % 2 === 1 ? '#f8fafc' : '#fff' }}>
                  <TableCell sx={{ ...cellSx, fontWeight: 600, fontFamily: 'monospace' }}>{row.name}</TableCell>
                  <TableCell sx={cellSx}>{row.provider}</TableCell>
                  <TableCell sx={cellSx}>{row.purpose}</TableCell>
                  <TableCell sx={cellSx}>{row.category}</TableCell>
                  <TableCell sx={cellSx}>{row.duration}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <P>
          Microsoft Entra ID may set authentication and security cookies on Microsoft's domains during the sign-in
          process. These cookies are controlled by Microsoft and are not created, read, or controlled by Global
          Mobile Software LLC. Their purposes and retention periods are determined by Microsoft and may change as
          Microsoft updates its authentication services.
        </P>
      </Section>

      <Section number={4} title="Strictly Necessary Technologies">
        <P>
          The cookies currently identified for TifoXR CMS are used for authentication, security, session
          management, or hosting and routing functionality necessary to provide the CMS.
        </P>
        <P>
          These technologies may be required to authenticate authorized CMS users, maintain login sessions, protect
          authentication tokens, maintain account and platform security, support Azure hosting and routing, and
          provide core CMS functionality.
        </P>
        <P>
          Because these technologies are necessary for the operation of the CMS, disabling them may prevent
          authorized users from logging in or may affect CMS functionality.
        </P>
        <P>
          Where permitted by applicable law, strictly necessary cookies may be used without separate consent.
        </P>
      </Section>

      <Section number={5} title="Microsoft Entra ID Authentication">
        <P>TifoXR CMS uses Microsoft authentication functionality for authorized-user access.</P>
        <P>
          Microsoft Entra ID may set authentication and security cookies on Microsoft's domains during the sign-in
          process, including the cookies identified in Section 3.
        </P>
        <P>
          These Microsoft-controlled cookies are not created, read, or controlled by GMS. Their purposes, operation,
          and retention periods are determined by Microsoft and may change as Microsoft updates its authentication
          services.
        </P>
      </Section>

      <Section number={6} title="Microsoft Azure Hosting">
        <P>TifoXR CMS uses Microsoft Azure infrastructure.</P>
        <P>
          The ARRAffinity cookie supports Azure hosting and routing affinity and is classified as strictly necessary
          for the relevant CMS functionality.
        </P>
        <P>
          The cookie operates as part of the Azure infrastructure supporting the CMS and is used for hosting and
          routing purposes rather than advertising or user profiling.
        </P>
      </Section>

      <Section number={7} title="Analytics and Other Technologies">
        <P>
          The current TifoXR CMS cookie inventory provided by GMS identifies authentication, security, session, and
          Azure hosting/routing technologies.
        </P>
        <P>
          No separate analytics, advertising, or other non-essential cookies are identified in the current CMS
          cookie inventory.
        </P>
        <P>
          If additional non-essential cookies or similar technologies are introduced, GMS will update this Cookie
          Policy as appropriate and, where required by applicable law, obtain consent before using them.
        </P>
      </Section>

      <Section number={8} title="Advertising and Sponsored Content">
        <P>
          TifoXR may display advertising or sponsored content in applicable TifoXR environments. However, no
          advertising cookies or advertising-related technologies are identified in the current TifoXR CMS cookie
          inventory.
        </P>
        <P>
          The TifoXR DSA Information, Content Moderation, and Abuse Reporting page provides additional information
          regarding TifoXR advertising practices.
        </P>
      </Section>

      <Section number={9} title="Your Choices">
        <P>
          Because the cookies currently identified for TifoXR CMS are strictly necessary authentication, security,
          session, or hosting technologies, they are generally required for the CMS to function.
        </P>
        <P>
          You may use your browser settings to block, restrict, or delete cookies. However, doing so may prevent
          authorized users from logging in or may affect CMS functionality.
        </P>
        <P>
          If non-essential cookies are introduced in the future and applicable law requires consent, GMS will
          provide an appropriate consent or preference mechanism.
        </P>
      </Section>

      <Section number={10} title="Third-Party Technologies">
        <P>TifoXR CMS relies on third-party technologies for authentication and infrastructure.</P>
        <P>Microsoft Authentication Library (MSAL) supports authentication functionality within the CMS.</P>
        <P>
          Microsoft Entra ID provides authentication services and may set authentication and security cookies on
          Microsoft's domains.
        </P>
        <P>
          Microsoft Azure provides hosting and infrastructure services, including routing functionality associated
          with the ARRAffinity cookie.
        </P>
        <P>Third-party services may be subject to the applicable provider's own terms and privacy notices.</P>
      </Section>

      <Section number={11} title="Changes to This Cookie Policy">
        <P>
          We may update this Cookie Policy when the TifoXR CMS platform, cookie configuration, authentication
          architecture, technologies, or applicable legal requirements change.
        </P>
        <P>The "Last Updated" date will indicate when this Cookie Policy was most recently revised.</P>
        <P>
          Where required by applicable law, we will provide additional notice or obtain consent for material
          changes.
        </P>
      </Section>

      <Section number={12} title="Contact">
        <P>Questions about this Cookie Policy may be sent to:</P>
        <Box component="address" sx={{ fontStyle: 'normal', color: TEXT }}>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>Global Mobile Software LLC</Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>7014 13th Avenue, Suite 202</Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>Brooklyn, NY 11228</Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.7 }}>USA</Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.7, mt: 0.5 }}>
            <Link href="mailto:compliance@globalmobisoft.com" sx={{ fontWeight: 700 }}>
              compliance@globalmobisoft.com
            </Link>
          </Typography>
        </Box>
      </Section>
    </LegalPageLayout>
  );
}
